import { motion } from "framer-motion";
import { Calendar, Lock, Play, Users } from "lucide-react";

const C = {
  bg:        '#020617',
  surface:   'rgba(15,23,42,0.7)',
  border:    'rgba(255,255,255,0.06)',
  text:      '#f1f5f9',
  textSub:   '#94a3b8',
  textFaint: '#475569',
  cyan:      '#22d3ee',
  amber:     '#f59e0b',
  emerald:   '#10b981',
  rose:      '#fb7185',
};

const Corners = ({ colorTL = C.cyan, colorBR = C.amber, size = 7 }) => (
  <>
    <span style={{ position:'absolute', top:-1, left:-1, width:size, height:size, borderTop:`1.5px solid ${colorTL}`, borderLeft:`1.5px solid ${colorTL}` }}/>
    <span style={{ position:'absolute', bottom:-1, right:-1, width:size, height:size, borderBottom:`1.5px solid ${colorBR}`, borderRight:`1.5px solid ${colorBR}` }}/>
  </>
);

const buttonBaseStyle = {
  padding: "10px 22px",
  fontWeight: 700,
  fontSize: 13,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  whiteSpace: "nowrap",
  transition: "all 0.2s",
  cursor: "pointer",
};

const buttonDisabledStyle = {
  ...buttonBaseStyle,
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${C.border}`,
  color: C.textFaint,
  cursor: "not-allowed",
};

const buttonEnabledStyle = (color) => ({
  ...buttonBaseStyle,
  background: `${color}15`,
  border: `1px solid ${color}40`,
  color: color,
  cursor: "pointer",
});

const pillStyle = (color, borderColor) => ({
  padding: "4px 12px",
  background: `${color}10`,
  border: `1px solid ${borderColor || color + "30"}`,
  borderRadius: 999,
  fontSize: 11,
  color,
});


// Bouton CTA selon l'état
const CTAButton = ({ atelier, accent, isUnlocked, isHost, handleVideoAction }) => {
  if (!atelier.videoCallEnabled) return null;

  if (!isUnlocked) {
    return (
      <button style={buttonDisabledStyle} disabled aria-disabled="true">
        <Lock size={14} /> Verrouillé
      </button>
    );
  }

  const handleClick = () => handleVideoAction(atelier);
  const style = buttonEnabledStyle(accent);

  if (isHost) {
    return (
      <button style={style} onClick={handleClick} aria-label="Lancer la session">
        <Play size={14} /> Lancer la session
      </button>
    );
  }

  return (
    <button style={style} onClick={handleClick} aria-label="Rejoindre la session">
      <Users size={14} /> Rejoindre la session
    </button>
  );
};

// Liste des métadonnées (pills)
const MetaPills = ({ atelier, accent }) => {
  const pills = [
    { label: `${atelier.duration} min`, color: accent },
    { label: `${atelier.exercises?.length || 0} exercices`, color: C.emerald },
    { label: "Live interactif", color: C.amber },
    { label: atelier.level, color: C.textSub, border: "rgba(255,255,255,0.1)" },
  ].filter((p) => p.label);

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
      {pills.map(({ label, color, border }) => (
        <div key={label} style={pillStyle(color, border)}>
          {label}
        </div>
      ))}
    </div>
  );
};

// Prévisualisation vidéo
const VideoPreview = ({ atelierVideo, accent, isMobile }) => {
  if (!atelierVideo?.url) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.3 }}
      style={{
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${accent}30`,
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      <video
        src={atelierVideo.url}
        controls
        poster={atelierVideo.thumbnail}
        style={{
          width: "100%",
          height: isMobile ? 240 : 280,
          display: "block",
          objectFit: "cover",
          background: "#000",
        }}
      />
      <div style={{ padding: isMobile ? "12px 14px" : "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <Play size={12} color={accent} />
          <span style={{ fontSize: 11, fontWeight: 600, color: accent, textTransform: "uppercase", letterSpacing: 1 }}>
            Dernière vidéo
          </span>
        </div>
        <h4 style={{
          fontSize: isMobile ? 14 : 15,
          fontWeight: 700,
          margin: 0,
          color: C.textLight,
          lineHeight: 1.35,
        }}>
          {atelierVideo.title || "Regarder la dernière vidéo"}
        </h4>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, color: C.textFaint, fontSize: 11 }}>
          {atelierVideo.duration && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Calendar size={10} /> {atelierVideo.duration}
            </span>
          )}
          {atelierVideo.date && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Calendar size={10} /> {atelierVideo.date}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};


const HeroCompact = ({
  atelier,
  accent,
  isUnlocked,
  isHost,
  handleVideoAction,
  isMobile = false,
  atelierVideo,
}) => {
  // Styles spécifiques au container (peuvent être externalisés)
  const containerStyle = {
    position: "relative",
    overflow: "hidden",
    padding: isMobile ? "20px 18px" : "28px 32px",
    background: `linear-gradient(135deg, ${accent}08 0%, rgba(2,6,23,0) 70%)`,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    marginBottom: 20,
  };

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Effets de fond (inchangés, mais on pourrait aussi les externaliser) */}
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 15% 50%, ${accent}10 0%, transparent 55%)`, pointerEvents: "none" }} />
      <div style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `linear-gradient(to right, ${accent}05 1px, transparent 1px), linear-gradient(to bottom, ${accent}05 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
        opacity: 0.35,
        maskImage: "radial-gradient(ellipse at 15% 50%, black 0%, transparent 60%)",
        WebkitMaskImage: "radial-gradient(ellipse at 15% 50%, black 0%, transparent 60%)",
        pointerEvents: "none",
      }} />

      <div style={{ position: "relative", zIndex: 2 }}>
        {/* Titre + icône */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <span style={{ fontSize: isMobile ? 36 : 44, lineHeight: 1, flexShrink: 0, filter: `drop-shadow(0 0 12px ${accent}60)` }}>
            {atelier.icon}
          </span>
          <div>
            <h1 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: isMobile ? 22 : 30,
              fontWeight: 800,
              background: `linear-gradient(90deg, ${accent}, ${C.amber} 70%, ${accent}80)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "shimmer 4s linear infinite",
              margin: 0,
              lineHeight: 1.1,
              letterSpacing: "-0.025em",
            }}>
              {atelier.title}
            </h1>
            <p style={{ fontSize: 12, color: `${accent}80`, margin: "4px 0 0", fontFamily: "'Roboto Mono', monospace", letterSpacing: "0.02em" }}>
              {atelier.subtitle}
            </p>
          </div>
        </div>

        {/* Layout à deux colonnes */}
        <div style={{
          display: isMobile ? "flex" : "grid",
          flexDirection: "column",
          gridTemplateColumns: "1fr 1fr",
          gap: 32,
        }}>
          {/* Colonne gauche : description + CTA */}
          <div style={{ marginBottom: isMobile ? 32 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <MetaPills atelier={atelier} accent={accent} />
              <p style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: isMobile ? 13.5 : 14,
                color: C.textSub,
                lineHeight: 1.78,
                margin: 0,
              }}>
                {atelier.description}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {isHost && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    background: `${C.amber}08`,
                    border: `1px solid ${C.amber}20`,
                    borderRadius: 6,
                  }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.amber, animation: "pulse-dot 2s ease-in-out infinite" }} />
                    <span>Hôte</span>
                  </div>
                )}
                <CTAButton
                  atelier={atelier}
                  accent={accent}
                  isUnlocked={isUnlocked}
                  isHost={isHost}
                  handleVideoAction={handleVideoAction}
                />
              </div>
            </div>
          </div>

          {/* Colonne droite : vidéo */}
          <VideoPreview atelierVideo={atelierVideo} accent={accent} isMobile={isMobile} />
        </div>
      </div>

      {/* Composant Corners (inchangé) */}
      <Corners colorTL={accent} colorBR={C.amber} size={8} />
    </motion.div>
  );
};

export default HeroCompact;